/**
 * The salvage beach.
 *
 * One shared idea drives everything here: `bedHeight(x, z)`. It is the sand as
 * a function of position — deep water offshore, rising through the surf to a
 * dry berm ashore. The visible sand mesh is a height field sampled from it, and
 * every floating body in this mode carries the same function as its
 * `bedSampler`, so a log rests exactly where the drawn sand meets it and floats
 * free at exactly the depth the shader shows as water.
 *
 * Geometry convention: the shore runs along X. Water lies toward -Z, land
 * toward +Z, and the waterline (bed = 0) sits at z = 0.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardNodeMaterial,
  Vector3,
} from 'three/webgpu'
import { color, mix, positionWorld, smoothstep } from 'three/tsl'

const SLOPE = 0.06
const BERM_HEIGHT = 3.6
// Continue the offshore seabed below the visible beach for ocean-going hulls.
const DEEP = -30

/** Seabed height at a world XZ. The single source of truth for the shore. */
export function bedHeight(x: number, z: number): number {
  const ramp = z * SLOPE
  // Gentle lateral undulation so the beach isn't a perfect ramp and logs come
  // to rest at varied heights; kept small so it never digs a channel that
  // strands a log underwater, and faded out in deep water where it can't be
  // seen anyway.
  const shoreFactor = Math.min(Math.max((z + 45) / 85, 0), 1)
  const undulation = (Math.sin(x * 0.02) * 0.55 + Math.sin(x * 0.055 + 1.3) * 0.25) * shoreFactor
  return Math.min(Math.max(ramp + undulation, DEEP), BERM_HEIGHT)
}

const X_MIN = -240
const X_MAX = 240
const Z_MIN = -150
const Z_MAX = 80
const COLS = 120
const ROWS = 70

export interface BoomTarget {
  position: Vector3
  radius: number
}

export class Beach {
  readonly group = new Group()
  /** Where the freed logs are delivered. */
  readonly boom: BoomTarget
  /** Grounded-log start positions, up in the surf and on the wet sand. */
  readonly logSpawns: { position: Vector3; heading: number }[]
  /** Deep-water start for the tug, facing the shore. */
  readonly tugSpawn = { position: new Vector3(0, 0, -135), heading: 0 }

  constructor(options: { boom?: boolean } = {}) {
    this.group.name = 'beach'
    this.group.add(this.buildSand())

    // Collection boom: a floating rectangle of yellow floats in deep water.
    this.boom = { position: new Vector3(0, 0, -110), radius: 26 }
    if (options.boom ?? true) this.group.add(this.buildBoom(this.boom))

    // Logs strewn along the wet sand and low surf.
    // Up on the wet sand and low surf — grounded but reachable with a line.
    this.logSpawns = [
      { position: new Vector3(-70, 0, 16), heading: 0.6 },
      { position: new Vector3(-20, 0, 22), heading: -0.3 },
      { position: new Vector3(35, 0, 18), heading: 1.2 },
      { position: new Vector3(85, 0, 20), heading: 0.1 },
      { position: new Vector3(140, 0, 15), heading: -0.8 },
    ]
  }

  private buildSand(): Mesh {
    const cols = COLS + 1
    const rows = ROWS + 1
    const positions = new Float32Array(cols * rows * 3)

    let p = 0
    for (let j = 0; j < rows; j++) {
      const z = Z_MIN + ((Z_MAX - Z_MIN) * j) / ROWS
      for (let i = 0; i < cols; i++) {
        const x = X_MIN + ((X_MAX - X_MIN) * i) / COLS
        positions[p] = x
        positions[p + 1] = bedHeight(x, z)
        positions[p + 2] = z
        p += 3
      }
    }

    const indices = new Uint32Array(COLS * ROWS * 6)
    let t = 0
    for (let j = 0; j < ROWS; j++) {
      for (let i = 0; i < COLS; i++) {
        const a = j * cols + i
        const b = a + 1
        const c = a + cols
        const d = c + 1
        indices[t++] = a
        indices[t++] = c
        indices[t++] = b
        indices[t++] = b
        indices[t++] = c
        indices[t++] = d
      }
    }

    const geometry = new BufferGeometry()
    geometry.setAttribute('position', new BufferAttribute(positions, 3))
    geometry.setIndex(new BufferAttribute(indices, 1))
    geometry.computeVertexNormals()

    const material = new MeshStandardNodeMaterial({ roughness: 0.98 })
    // Wet sand at the waterline, drying out as it climbs the berm.
    const wet = color(new Color('#8a7a54'))
    const dry = color(new Color('#d9c48f'))
    material.colorNode = mix(wet, dry, smoothstep(0.15, 1.6, positionWorld.y))

    const mesh = new Mesh(geometry, material)
    mesh.receiveShadow = true
    mesh.name = 'sand'
    return mesh
  }

  private buildBoom(boom: BoomTarget): Group {
    const group = new Group()
    group.name = 'boom'
    const floatMat = new MeshStandardNodeMaterial({ color: new Color('#e8b422') })
    floatMat.roughness = 0.6

    // A ring of float drums marking the collection pen.
    const count = 12
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2
      const drum = new Mesh(new CylinderGeometry(1.3, 1.3, 2.4, 10), floatMat)
      drum.rotation.z = Math.PI / 2
      drum.rotation.y = a
      drum.position.set(
        boom.position.x + Math.cos(a) * boom.radius,
        0.2,
        boom.position.z + Math.sin(a) * boom.radius,
      )
      group.add(drum)
    }
    return group
  }

  /** Bob the boom floats on the swell. Called once per rendered frame. */
  update(_elapsed: number): void {
    // Static for now; the floats read fine sitting at the mean surface.
  }
}
